import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, FileText, Sparkles } from 'lucide-react';
import PageLayout from '../../components/layout/PageLayout';
import { VUKZ } from '../../theme/vukzTheme';
import {
  createLibraryItem,
  ITEM_TYPES,
  EVIDENCE_LEVELS,
  SPORT_LABELS,
} from '../../services/libraryService';

// ─── Helpers ─────────────────────────────────────────────────────────

const INITIAL_FORM = {
  title: '',
  item_type: 'protocol',
  sport: 'swimming',
  evidence_level: 'B',
  category: '',
  description: '',
  tags: '',
  content_json: '',
};

// ─── Reusable form field wrapper ─────────────────────────────────────

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold mb-1.5" style={{ color: VUKZ.text }}>
        {label}
        {required && <span style={{ color: VUKZ.error }}> *</span>}
      </label>
      {children}
      {hint && (
        <p className="mt-1 text-xs" style={{ color: VUKZ.textLight }}>{hint}</p>
      )}
    </div>
  );
}

// ─── Page Component ──────────────────────────────────────────────────

export default function LibraryCreateItemPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ── Handlers ───────────────────────────────────────────────────────

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError('O título é obrigatório.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Build payload
      const payload = {
        title: form.title.trim(),
        item_type: form.item_type,
        sport: form.sport,
        evidence_level: form.evidence_level,
        category: form.category.trim() || null,
        description: form.description.trim() || null,
        tags: form.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      };

      // Parse content_json if provided
      if (form.content_json.trim()) {
        try {
          payload.content_json = JSON.parse(form.content_json);
        } catch {
          setError('O campo Conteúdo JSON contém JSON inválido.');
          setSaving(false);
          return;
        }
      }

      const result = await createLibraryItem(payload);

      // Navigate to newly created item or back to hub
      const newId = result?.data?.item_id || result?.item_id;
      if (newId) {
        navigate(`/library/item/${newId}`);
      } else {
        navigate('/library');
      }
    } catch (err) {
      console.error('[LibraryCreate] Error:', err);
      setError(err?.response?.data?.error || 'Erro ao criar item. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  // ── Input styling ──────────────────────────────────────────────────

  const inputClass =
    'w-full px-4 py-2.5 rounded-xl border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1';

  const inputStyle = {
    borderColor: VUKZ.border,
    color: VUKZ.text,
    backgroundColor: '#fff',
  };

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <PageLayout
      header={{
        title: 'Novo Item',
        subtitle: 'Adicione conhecimento à biblioteca digital',
        actions: (
          <Link
            to="/library"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold
                       transition-all duration-200 hover:opacity-80 border"
            style={{ borderColor: VUKZ.border, color: VUKZ.text }}
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Link>
        ),
      }}
    >
      <form onSubmit={handleSubmit} className="space-y-8 max-w-3xl mx-auto">
        {/* ── Error Banner ────────────────────────────────────────── */}
        {error && (
          <div
            className="px-4 py-3 rounded-xl text-sm font-medium border"
            style={{
              backgroundColor: '#fef2f2',
              borderColor: '#fecaca',
              color: VUKZ.error,
            }}
          >
            {error}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════
            SECTION 1 — Informações Básicas
           ═══════════════════════════════════════════════════════════ */}
        <section
          className="rounded-2xl border p-6 md:p-8 space-y-5"
          style={{ borderColor: VUKZ.border, backgroundColor: '#fff' }}
        >
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-5 h-5" style={{ color: VUKZ.primary }} />
            <h2 className="text-base font-bold uppercase tracking-widest" style={{ color: VUKZ.textLight }}>
              Informações Básicas
            </h2>
          </div>

          {/* Title */}
          <Field label="Título" required hint="Nome descritivo do item (ex: Protocolo de Velocidade Sprint 50m)">
            <input
              type="text"
              value={form.title}
              onChange={handleChange('title')}
              placeholder="Ex: Protocolo de Velocidade Sprint 50m"
              className={inputClass}
              style={inputStyle}
              autoFocus
            />
          </Field>

          {/* Type + Sport row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Tipo" required>
              <select
                value={form.item_type}
                onChange={handleChange('item_type')}
                className={inputClass}
                style={inputStyle}
              >
                {Object.entries(ITEM_TYPES).map(([key, meta]) => (
                  <option key={key} value={key}>
                    {meta.label} — {meta.description}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Esporte">
              <select
                value={form.sport}
                onChange={handleChange('sport')}
                className={inputClass}
                style={inputStyle}
              >
                {Object.entries(SPORT_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {/* Evidence + Category row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Nível de Evidência" hint="Classificação científica do conteúdo">
              <select
                value={form.evidence_level}
                onChange={handleChange('evidence_level')}
                className={inputClass}
                style={inputStyle}
              >
                {Object.entries(EVIDENCE_LEVELS).map(([key, meta]) => (
                  <option key={key} value={key}>
                    {meta.badge} — {meta.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Categoria" hint="Tag principal (ex: velocidade, resistência)">
              <input
                type="text"
                value={form.category}
                onChange={handleChange('category')}
                placeholder="Ex: velocidade"
                className={inputClass}
                style={inputStyle}
              />
            </Field>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════
            SECTION 2 — Conteúdo
           ═══════════════════════════════════════════════════════════ */}
        <section
          className="rounded-2xl border p-6 md:p-8 space-y-5"
          style={{ borderColor: VUKZ.border, backgroundColor: '#fff' }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5" style={{ color: VUKZ.primary }} />
            <h2 className="text-base font-bold uppercase tracking-widest" style={{ color: VUKZ.textLight }}>
              Conteúdo
            </h2>
          </div>

          {/* Description */}
          <Field label="Descrição" hint="Resumo do item — será exibido nos cards e resultados de busca">
            <textarea
              rows={4}
              value={form.description}
              onChange={handleChange('description')}
              placeholder="Descreva o objetivo, público-alvo e aplicação..."
              className={inputClass + ' resize-y'}
              style={inputStyle}
            />
          </Field>

          {/* Tags */}
          <Field label="Tags" hint="Separadas por vírgula (ex: sprint, 50m, velocidade)">
            <input
              type="text"
              value={form.tags}
              onChange={handleChange('tags')}
              placeholder="sprint, 50m, velocidade"
              className={inputClass}
              style={inputStyle}
            />
          </Field>

          {/* Content JSON */}
          <Field
            label="Conteúdo Estruturado (JSON)"
            hint="Opcional — dados estruturados do item (séries, zonas, parâmetros)"
          >
            <textarea
              rows={8}
              value={form.content_json}
              onChange={handleChange('content_json')}
              placeholder={'{\n  "series": [\n    { "reps": 4, "distance": "50m", "rest": "60s" }\n  ]\n}'}
              className={inputClass + ' resize-y font-mono text-xs'}
              style={inputStyle}
            />
          </Field>
        </section>

        {/* ═══════════════════════════════════════════════════════════
            ACTIONS
           ═══════════════════════════════════════════════════════════ */}
        <div className="flex items-center justify-between gap-4 pb-8">
          <Link
            to="/library"
            className="px-5 py-2.5 rounded-xl text-sm font-semibold border transition-all hover:opacity-80"
            style={{ borderColor: VUKZ.border, color: VUKZ.textLight }}
          >
            Cancelar
          </Link>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold
                       transition-all duration-200 hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: VUKZ.primary, color: '#fff' }}
          >
            {saving ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Salvando…
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Criar Item
              </>
            )}
          </button>
        </div>
      </form>
    </PageLayout>
  );
}
